package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminStaffRole
import org.gymly.model.AdminSettingsUser
import org.gymly.model.AdminSettingsUserStatus
import org.gymly.model.AdminSettingsUserType
import org.gymly.model.AdminSettingsUserUpsert
import org.gymly.theme.AdminColors
import org.gymly.util.isValidEmailOrBlank
import org.gymly.util.userMessage
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible

private sealed class UserDialog {
    data object Create : UserDialog()
    data class Edit(val item: AdminSettingsUser) : UserDialog()
    data class Detail(val item: AdminSettingsUser) : UserDialog()
    data class Delete(val item: AdminSettingsUser) : UserDialog()
}

@Composable
internal fun SettingsUsersPanel(dateFilterDays: Int?, compact: Boolean) {
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminSettingsUser>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dialog by remember { mutableStateOf<UserDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listSettingsUsers()
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los usuarios.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val filtered = remember(items, query, dateFilterDays) {
        val q = query.trim().lowercase()
        val cutoff = dateFilterDays?.let { Clock.System.now() - it.days }
        items.filter { u ->
            val hay = listOfNotNull(
                u.displayName,
                u.email,
                u.type.labelEs(),
                u.roleDisplay(),
                u.status.labelEs(),
            ).joinToString(" ").lowercase()
            val matchesQuery = q.isEmpty() || hay.contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = u.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilterDays),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "type" -> { u -> u.type.labelEs().lowercase() }
                "role" -> { u -> u.roleDisplay().lowercase() }
                "status" -> { u -> u.status.labelEs().lowercase() }
                else -> { u -> u.displayName.lowercase() }
            }
        }
    }

    val total = filtered.size
    val clients = filtered.count { it.type == AdminSettingsUserType.CLIENT }
    val trainers = filtered.count { it.type == AdminSettingsUserType.TRAINER }
    val staff = filtered.count { it.type == AdminSettingsUserType.STAFF }

    Column(Modifier.fillMaxSize()) {
        UsersKpiRow(total, clients, trainers, staff, compact)
        Spacer(Modifier.height(16.dp))
        error?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
        }
        UsersDirectoryCard(
            users = pageItems,
            totalCount = filtered.size,
            listPaging = listPaging,
            loading = loading,
            query = query,
            onQueryChange = { query = it },
            compact = compact,
            onCreate = { dialog = UserDialog.Create },
            onView = { dialog = UserDialog.Detail(it) },
            onEdit = { dialog = UserDialog.Edit(it) },
            onDelete = { dialog = UserDialog.Delete(it) },
        )
    }

    when (val d = dialog) {
        is UserDialog.Detail -> UserDetailDialog(d.item) { dialog = null }
        is UserDialog.Create -> UserFormDialog(
            title = "Agregar usuario",
            initial = null,
            companyId = companyId,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createSettingsUser(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo crear el usuario.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is UserDialog.Edit -> UserFormDialog(
            title = "Editar usuario",
            initial = d.item,
            companyId = companyId,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateSettingsUser(d.item.type, d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar el usuario.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is UserDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar usuario") },
            text = {
                Text("¿Seguro que deseas eliminar a ${d.item.displayName}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteSettingsUser(d.item.type, d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar el usuario.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun UsersKpiRow(
    total: Int,
    clients: Int,
    trainers: Int,
    staff: Int,
    compact: Boolean,
) {
    val cards = listOf(
        Triple("Total de usuarios", total.toString(), AdminColors.Primary),
        Triple("Clientes", clients.toString(), Color(0xFF1565C0)),
        Triple("Entrenadores", trainers.toString(), Color(0xFF2E7D32)),
        Triple("Personal", staff.toString(), Color(0xFF6A1B9A)),
    )
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            cards.chunked(2).forEach { row ->
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    row.forEach { (label, value, accent) ->
                        UserKpiCard(label, value, accent, Modifier.weight(1f))
                    }
                }
            }
        }
    } else {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            cards.forEach { (label, value, accent) ->
                UserKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun UserKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.UsersDirectoryCard(
    users: List<AdminSettingsUser>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminSettingsUser) -> Unit,
    onEdit: (AdminSettingsUser) -> Unit,
    onDelete: (AdminSettingsUser) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Directorio de usuarios", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                UserSearchField(query, onQueryChange)
                Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nuevo usuario", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Directorio de usuarios", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(Modifier.widthIn(max = 220.dp)) { UserSearchField(query, onQueryChange) }
                    Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nuevo usuario", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay usuarios todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    users.forEach { u -> UserMobileCard(u, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                UsersTable(
                    users,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun UserSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun UsersTable(
    users: List<AdminSettingsUser>,
    listPaging: AdminListPaging,
    onView: (AdminSettingsUser) -> Unit,
    onEdit: (AdminSettingsUser) -> Unit,
    onDelete: (AdminSettingsUser) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Usuario", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
            AdminSortableHeader("Tipo", "type", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminSortableHeader("Rol", "role", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            users.forEach { u ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.6f), verticalAlignment = Alignment.CenterVertically) {
                        UserTypeDot(u.type)
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(u.displayName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(u.email ?: "—", color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Text(u.type.labelEs(), Modifier.weight(0.9f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Text(u.roleDisplay(), Modifier.weight(1f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.8f)) { UserStatusBadge(u.status) }
                    Row(Modifier.width(140.dp), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                        IconButton(onClick = { onView(u) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { onEdit(u) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { onDelete(u) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun UserMobileCard(
    item: AdminSettingsUser,
    onView: (AdminSettingsUser) -> Unit,
    onEdit: (AdminSettingsUser) -> Unit,
    onDelete: (AdminSettingsUser) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            UserTypeDot(item.type)
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(item.displayName, fontWeight = FontWeight.SemiBold)
                Text(item.email ?: "—", color = AdminColors.TextMuted, fontSize = 12.sp)
                Text(
                    "${item.type.labelEs()} · ${item.roleDisplay()}",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            UserStatusBadge(item.status)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
            }
            IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun UserTypeDot(type: AdminSettingsUserType) {
    val color = when (type) {
        AdminSettingsUserType.CLIENT -> Color(0xFF1565C0)
        AdminSettingsUserType.STAFF -> Color(0xFF6A1B9A)
        AdminSettingsUserType.TRAINER -> Color(0xFF2E7D32)
    }
    Box(
        Modifier
            .size(10.dp)
            .clip(CircleShape)
            .background(color),
    )
}

@Composable
private fun UserStatusBadge(status: AdminSettingsUserStatus) {
    val active = status == AdminSettingsUserStatus.ACTIVE
    Text(
        status.labelEs(),
        color = if (active) Color(0xFF1B5E20) else AdminColors.TextMuted,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(if (active) Color(0xFFE8F5E9) else AdminColors.Outline.copy(alpha = 0.25f))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun UserDetailDialog(item: AdminSettingsUser, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Detalle del usuario", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            UserDetailLine("Nombre completo", item.displayName)
            UserDetailLine("Correo", item.email ?: "—")
            UserDetailLine("Tipo de usuario", item.type.labelEs())
            UserDetailLine("Rol", item.roleDisplay())
            UserDetailLine("Estado", item.status.labelEs())
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
private fun UserDetailLine(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun UserFormDialog(
    title: String,
    initial: AdminSettingsUser?,
    companyId: String?,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminSettingsUserUpsert) -> Unit,
) {
    var displayName by remember(initial) { mutableStateOf(initial?.displayName.orEmpty()) }
    var email by remember(initial) { mutableStateOf(initial?.email.orEmpty()) }
    var type by remember(initial) { mutableStateOf(initial?.type ?: AdminSettingsUserType.CLIENT) }
    var role by remember(initial) {
        mutableStateOf(
            initial?.staffRole ?: when (initial?.type) {
                AdminSettingsUserType.TRAINER -> AdminStaffRole.TRAINER
                AdminSettingsUserType.STAFF -> AdminStaffRole.RECEPTION
                else -> null
            },
        )
    }
    val branchId = companyId
    var status by remember(initial) { mutableStateOf(initial?.status ?: AdminSettingsUserStatus.ACTIVE) }
    var password by remember(initial) { mutableStateOf("") }
    var inactivationReason by remember(initial) { mutableStateOf<String?>(null) }
    var inactivationNote by remember(initial) { mutableStateOf("") }
    var typeMenu by remember { mutableStateOf(false) }
    var roleMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }
    var formError by remember { mutableStateOf<String?>(null) }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .heightIn(max = 640.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))

            Text("Nombre completo *", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = displayName,
                onValueChange = { displayName = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )

            Spacer(Modifier.height(12.dp))
            Text("Correo", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = email,
                onValueChange = { email = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )

            Spacer(Modifier.height(12.dp))
            Text("Tipo de usuario", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { typeMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    enabled = !saving,
                ) {
                    Text(type.labelEs(), color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                }
                DropdownMenu(expanded = typeMenu, onDismissRequest = { typeMenu = false }) {
                    AdminSettingsUserType.entries.forEach { t ->
                        DropdownMenuItem(
                            text = { Text(t.labelEs()) },
                            onClick = {
                                type = t
                                role = when (t) {
                                    AdminSettingsUserType.CLIENT -> null
                                    AdminSettingsUserType.TRAINER -> AdminStaffRole.TRAINER
                                    AdminSettingsUserType.STAFF ->
                                        role?.takeUnless { it == AdminStaffRole.TRAINER } ?: AdminStaffRole.RECEPTION
                                }
                                typeMenu = false
                            },
                        )
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text("Rol", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            if (type == AdminSettingsUserType.CLIENT) {
                OutlinedTextField(
                    value = "Cliente",
                    onValueChange = {},
                    modifier = Modifier.fillMaxWidth(),
                    readOnly = true,
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Outline,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
            } else {
                Box {
                    OutlinedButton(
                        onClick = { roleMenu = true },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                        enabled = !saving,
                    ) {
                        Text((role ?: AdminStaffRole.RECEPTION).labelEs(), color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                    }
                    DropdownMenu(expanded = roleMenu, onDismissRequest = { roleMenu = false }) {
                        AdminStaffRole.entries.forEach { r ->
                            DropdownMenuItem(
                                text = { Text(r.labelEs()) },
                                onClick = {
                                    role = r
                                    type = if (r == AdminStaffRole.TRAINER) {
                                        AdminSettingsUserType.TRAINER
                                    } else {
                                        AdminSettingsUserType.STAFF
                                    }
                                    roleMenu = false
                                },
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text("Estado", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { statusMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    enabled = !saving,
                ) {
                    Text(status.labelEs(), color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                }
                DropdownMenu(expanded = statusMenu, onDismissRequest = { statusMenu = false }) {
                    AdminSettingsUserStatus.entries.forEach { s ->
                        DropdownMenuItem(
                            text = { Text(s.labelEs()) },
                            onClick = {
                                status = s
                                statusMenu = false
                            },
                        )
                    }
                }
            }

            if (status == AdminSettingsUserStatus.INACTIVE) {
                Spacer(Modifier.height(12.dp))
                InactivationReasonFields(
                    reasons = if (type == AdminSettingsUserType.CLIENT) CLIENT_INACTIVATION_REASONS else STAFF_INACTIVATION_REASONS,
                    reason = inactivationReason,
                    onReasonChange = { inactivationReason = it },
                    note = inactivationNote,
                    onNoteChange = { inactivationNote = it },
                    enabled = !saving,
                )
            }

            Spacer(Modifier.height(12.dp))
            val passwordLabel = when (type) {
                AdminSettingsUserType.CLIENT ->
                    if (initial == null) "Contraseña app socio" else "Nueva contraseña app (opcional)"
                else ->
                    if (initial == null) "Contraseña de acceso *" else "Nueva contraseña (opcional)"
            }
            Text(passwordLabel, color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            val (visible, toggleVisible) = rememberPasswordVisible()
            OutlinedTextField(
                value = password,
                onValueChange = { password = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("Mínimo 8 caracteres", color = AdminColors.TextMuted) },
                visualTransformation = if (visible) VisualTransformation.None else PasswordVisualTransformation(),
                trailingIcon = { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            if (initial != null) {
                Text(
                    "Deja en blanco para mantener la contraseña actual.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }

            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            ) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (displayName.trim().isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        if (!email.isValidEmailOrBlank()) {
                            formError = "El correo debe incluir @ y un dominio (ej. nombre@correo.com)."
                            return@Button
                        }
                        val pwd = password.trim()
                        if (initial?.type == AdminSettingsUserType.CLIENT && type != AdminSettingsUserType.CLIENT) {
                            formError = "Un cliente mantiene el rol Cliente. El personal se cambia en su propio usuario."
                            return@Button
                        }
                        if (initial != null && initial.type != AdminSettingsUserType.CLIENT && type == AdminSettingsUserType.CLIENT) {
                            formError = "El personal no pasa a cliente desde este formulario."
                            return@Button
                        }
                        if (initial == null && type != AdminSettingsUserType.CLIENT && pwd.length < 8) {
                            formError = "La contraseña debe tener al menos 8 caracteres."
                            return@Button
                        }
                        if (pwd.isNotEmpty() && pwd.length < 8) {
                            formError = "La contraseña debe tener al menos 8 caracteres."
                            return@Button
                        }
                        val inactive = status == AdminSettingsUserStatus.INACTIVE
                        if (inactive && inactivationReason == null) {
                            formError = "Indica el motivo de la inactivación."
                            return@Button
                        }
                        if (inactive && inactivationReason == "OTRO" && inactivationNote.isBlank()) {
                            formError = "Describe el motivo de la inactivación."
                            return@Button
                        }
                        onSave(
                            AdminSettingsUserUpsert(
                                type = type,
                                displayName = displayName.trim(),
                                email = email.trim().ifBlank { null },
                                branchId = branchId,
                                status = status,
                                password = pwd.ifBlank { null },
                                staffRole = if (type == AdminSettingsUserType.CLIENT) null else role,
                                inactivationReason = if (inactive) inactivationReason else null,
                                inactivationNote = if (inactive) inactivationNote.trim().ifBlank { null } else null,
                            ),
                        )
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}
